<template>
  <div>
    <div
      :class="{
        [`${el}__teaser`]: true,
        [`${el}__teaser--${status}`]: !!status,
        [`${el}__teaser--inverted`]: currentBasicApplication.is,
        [`${el}__teaser--noMargin`]: !!first,
      }"
    >
      <div :class="`${el}__teaserContent`">
        <Copy :staticClass="`${el}__teaserHeadline`" element="div" size="l" bold>
          {{ $t(`applications.teaser.${id}.headline`) }}
        </Copy>
        <div :class="`${el}__teaserCopy`">
          <Copy element="div" color="tertiaryLight">
            {{ $t(`applications.teaser.${id}.copy`) }}
          </Copy>
          <Copy
            v-if="id === 'pflegehilfsmittel'"
            :staticClass="`${el}__teaserNotice`"
            element="div"
            color="tertiaryLight"
            size="s"
          >
            <span v-html="$t('applications.teaser.pflegehilfsmittel.notice')" />
          </Copy>
        </div>

        <span v-if="status && statusText" :class="`${el}__teaserStatusWrapper`">
          <Copy element="div" size="s" :staticClass="`${el}__teaserStatus`" color="primaryBlue">
            <Icon :name="statusMap[status]" />
            {{ statusText }}
          </Copy>
        </span>
        <span
          v-if="id === 'erstantrag' || id === 'hoeherstufung'"
          :class="`${el}__teaserDownloadLinkWrapper`"
        >
          <Link
            v-if="documents && documents.length > 0"
            @click="openDownloadArea"
            icon="file-download-line"
            :class="{
              [`${el}__teaserDownloadLink`]: true,
              [`${el}__teaserDownloadLink--withMargin`]: status && statusText,
            }"
          >
            {{ documents.length > 1 ? 'Eingereichte Anträge' : 'Eingereichter Antrag' }}
          </Link>
        </span>
      </div>

      <div :class="`${el}__teaserSelect`">
        <template v-if="id === 'entlastungsbetrag'">
          <Link @click="openContentSheet" icon="lightbulb-flash-line">
            {{ $t('applications.teaser.contentSheet.link3') }}
          </Link>
        </template>
        <template v-else>
          <template v-if="id === 'erstantrag' || id === 'hoeherstufung'">
            <Col :xs="4" :m="12">
              <Button
                type="outlined"
                transparent
                small
                :label="$t('applications.teaser.contentSheet.link1')"
                @click="openEdithCarePopUp"
                additionalIconName="arrow-right-line"
                :class="`${el}__teaserButton`"
              />
            </Col>
          </template>

          <Link @click="openContentSheet" icon="lightbulb-flash-line">
            {{ $t('applications.teaser.contentSheet.link2') }}
          </Link>
        </template>

        <Link @click="openModal" icon="restart-line">
          {{ $t('applications.teaser.modal.link') }}
        </Link>
      </div>
    </div>

    <Modal
      :headline="$t('applications.modal.headline')"
      :open="modal"
      @close="close('x')"
      size="s"
      overflow
    >
      <div :class="`${el}__modalContainer`">
        <div :class="`${el}__modalContainerElement`">
          <Copy element="div" bold centered>{{ $t('applications.modal.box2.headline') }}</Copy>
          <BaseSelect
            :class="`${el}__modalSelect`"
            @input="setActiveStatusItem"
            :items="statusItems"
            expand
            light
          />
        </div>
        <div
          v-if="isCareLevelApplication"
          :class="{
            [`${el}__modalContainerElement`]: true,
            [`${el}__modalContainerElement--error`]: careLevelError,
          }"
        >
          <Copy v-if="currentStatusItem.value === '3'" element="div" bold centered>
            {{
              $t('applications.modal.box1.headline.approvedCareLevel', {
                value: currentStatusItem.label,
              })
            }}
          </Copy>
          <Copy v-else element="div" bold centered>
            {{ $t('applications.modal.box1.headline.expectedCareLevel') }}
          </Copy>
          <div :class="`${el}__careLevelButtons`">
            <div
              v-for="i in 5"
              :key="`careLevel-${i}`"
              :class="{
                [`${el}__careLevelButton`]: true,
                [`${el}__careLevelButton--hasCareLevel`]: hasCareLevel,
                [`${el}__careLevelButton--active`]: i === applicationCareLevel,
              }"
              @click="onClickCareLevel(i)"
              tabindex="0"
              @keyup.enter="onClickCareLevel(i)"
              @keyup.space="onClickCareLevel(i)"
              role="radio"
              :aria-checked="i === careLevel"
            >
              {{ i }}
            </div>
          </div>
          <Copy
            v-if="careLevelError"
            staticClass="u-margin__top--3"
            element="div"
            color="supportDanger"
            bold
            centered
          >
            {{ $t('applications.modal.box1.error') }}
          </Copy>
        </div>
        <div
          ref="date"
          :class="{
            [`${el}__modalContainerElementClosable`]: true,
            [`${el}__modalContainerElementClosable--show`]: showDate,
          }"
        >
          <div :class="`${el}__modalContainerElement `">
            <Copy element="div" bold centered>
              {{ $t('applications.modal.box3.headline', { value: currentStatusItem.label }) }}
            </Copy>
            <div :class="`${el}__applicationDateDatePicker`">
              <Datepicker
                v-model="applicationDate"
                :name="`applicationDate-${id}`"
                :language="de"
                :format="$t('datepicker.format')"
                :placeholder="$t('datepicker.placeholder')"
                :disabledDates="disabledDates"
              />
              <Copy
                v-if="dateError"
                :staticClass="`${el}__helperText`"
                element="div"
                size="s"
                color="supportDanger"
              >
                {{ $t('applications.modal.box3.error') }}
              </Copy>
            </div>
          </div>
        </div>
        <div
          :class="{
            [`${el}__modalContainerElementClosable`]: true,
            [`${el}__modalContainerElementClosable--show`]: willUpdateCareLevel,
          }"
        >
          <Alert background="secondaryYellow" :headline="$t('applications.modal.box2.notice')" />
        </div>
      </div>

      <div :class="`${el}__modalButtonRow`">
        <Button type="outlined" label="Abbrechen" small @click="close('button')" />
        <Button label="Speichern" small @click="onSave" :loading="loading" />
      </div>
    </Modal>
  </div>
</template>

<script>
import Vue from 'vue';
import { de } from 'vuejs-datepicker/dist/locale';
import Datepicker from 'vuejs-datepicker';

import Copy from '@/objects/Copy';
import Link from '@/objects/Link';
import Button from '@/objects/Button';
import Icon from '@/objects/Icon';
import Alert from '@/objects/Alert';
import BaseSelect from '@/objects/Forms/BaseSelect';
import Modal from '@/components/Modal';

import { CONTENT_SHEET_CONTENT } from '@/store/actions/contentSheet';
import { APPLICATION_SAVE, TOGGLE_EDITHCARE_POPUP } from '@/store/actions/application';
import { HEALTH_REQUEST } from '@/store/actions/health';
import { TASKS_UPDATE_CURRENT_TASK } from '@/store/actions/tasks';
import { CARESERVICE_UPDATE_FACT } from '@/store/actions/careService';
import {
  mapFieldsApplication,
  mapFieldsHealth,
  mapFieldsCareService,
  mapFieldsContentSheet,
} from '@/store/mapFields';

import {
  ROUTE_API_EDITHCARE,
  ROUTE_API_UPDATE_CARE_LEVEL_DATA,
  ROUTE_GET_EDITHCARE_PDF,
} from '@/constants/apiRoutes';

import { createLinkLabel, createDownloadLink } from '@/helpers/edithcare';
import { formatDate } from '@/helpers/formatter';
import Col from '@/components/Grid/Col/Col';

export default {
  name: 'ApplicationTeaser',
  props: {
    el: String,
    id: String,
    first: Boolean,
    documents: {
      type: Array,
      required: false,
    },
    token: {
      type: String,
      required: false,
    },
  },
  components: {
    Col,
    Copy,
    Link,
    Button,
    BaseSelect,
    Icon,
    Alert,
    Modal,
    Datepicker,
  },

  computed: {
    ...mapFieldsCareService(['careServiceFacts']),
    ...mapFieldsApplication(['applications']),
    ...mapFieldsHealth(['healthUuid', 'careLevel']),
    ...mapFieldsContentSheet(['contentSheetIsOpen']),

    currentBasicApplication() {
      let pflegegeldDate = null;
      let pflegesachleistungenDate = null;
      let kombinationsleistungDate = null;
      let current = '';

      if (this.applications.pflegegeld.appliedAt && this.applications.pflegegeld.status === 3) {
        pflegegeldDate = new Date(this.applications.pflegegeld.appliedAt);
      }

      if (
        this.applications.pflegesachleistungen.appliedAt &&
        this.applications.pflegesachleistungen.status === 3
      ) {
        pflegesachleistungenDate = new Date(this.applications.pflegesachleistungen.appliedAt);
      }

      if (
        this.applications.kombinationsleistung.appliedAt &&
        this.applications.kombinationsleistung.status === 3
      ) {
        kombinationsleistungDate = new Date(this.applications.kombinationsleistung.appliedAt);
      }

      if (pflegegeldDate > pflegesachleistungenDate && pflegegeldDate > kombinationsleistungDate) {
        current = 'pflegegeld';
      }
      if (
        pflegesachleistungenDate > pflegegeldDate &&
        pflegesachleistungenDate > kombinationsleistungDate
      ) {
        current = 'pflegesachleistungen';
      }
      if (
        kombinationsleistungDate > pflegegeldDate &&
        kombinationsleistungDate > pflegesachleistungenDate
      ) {
        current = 'kombinationsleistung';
      }

      const ids = ['pflegegeld', 'pflegesachleistungen', 'kombinationsleistung'];
      return { is: ids.includes(this.id) && current !== this.id, id: current };
    },

    status() {
      const { status } = this.current;
      if (!status) {
        return false;
      }
      return ['neutral1', 'warning', 'success', 'danger', 'neutral2'][status - 1];
    },

    statusText() {
      const { status, appliedAt } = this.current;
      if (!status) return false;

      let text = '';
      if (status === 1) text = 'geplant';
      if (status === 2) text = 'beantragt';
      if (status === 3) text = 'genehmigt';
      if (status === 4) text = 'abgelehnt';
      if (status === 5) text = 'nicht beantragt';

      if (appliedAt) {
        text += ` am ${formatDate(appliedAt)}`;
      }

      return text;
    },

    willUpdateCareLevel() {
      const { value } = this.currentStatusItem;
      return this.isCareLevelApplication && value === '3' && this.applicationCareLevel;
    },

    current() {
      return this.applications[this.id];
    },

    hasCareLevel() {
      return this.current.careLevel !== null;
    },

    isCareLevelApplication() {
      return this.id === 'erstantrag' || this.id === 'hoeherstufung';
    },

    currentStatusItem() {
      return this.statusItems.filter((si) => si.selected)[0];
    },

    showDate() {
      const { value } = this.currentStatusItem;
      const options = ['1', '2', '3', '4'];
      return options.includes(value);
    },
  },
  watch: {
    showDate() {
      if (this.showDate) {
        // For a clean transition
        setTimeout(() => {
          this.$refs.date.style.overflow = 'visible';
        }, 400);
      } else {
        this.$refs.date.removeAttribute('style');
      }
    },

    applicationCareLevel() {
      if (this.applicationCareLevel && this.careLevelError) {
        this.careLevelError = false;
      }
    },

    applicationDate() {
      if (this.applicationDate && this.dateError) {
        this.dateError = false;
      }
    },

    'current.status': function () {
      this.setModalValues();
    },
  },

  data() {
    return {
      ROUTE_API_EDITHCARE,
      formatDate,
      createLinkLabel,
      createDownloadLink,
      loading: false,
      modal: false,
      careLevelError: false,
      dateError: false,
      applicationCareLevel: null,
      applicationDate: null,
      de,
      disabledDates: {
        from: new Date(Date.now()),
      },
      statusItems: [
        {
          label: 'keine Angabe',
          value: '0',
          selected: true,
        },
        {
          label: 'geplant',
          value: '1',
        },
        {
          label: 'beantragt',
          value: '2',
        },
        {
          label: 'genehmigt',
          value: '3',
        },
        {
          label: 'abgelehnt',
          value: '4',
        },
        {
          label: 'nicht beantragt',
          value: '5',
        },
      ],
      statusMap: {
        danger: 'close-circle-fill',
        neutral1: 'calendar-todo-fill',
        success: 'checkbox-circle-fill',
        neutral2: 'indeterminate-circle-fill',
        warning: 'time-fill',
      },
    };
  },

  mounted() {
    this.setModalValues();
    const links = [...document.querySelectorAll('[data-tracking]')];

    links.map((link) => {
      if (link.dataset.tracking_id === this.id) {
        link.addEventListener('click', () => {
          this.onTrackingClick(link.dataset.tracking, 'link');
        });
      }
    });
  },
  beforeDestroy() {
    const links = [...document.querySelectorAll('[data-tracking]')];
    links.map((link) => {
      link.removeEventListener('click', () => {
        this.onTrackingClick(link.dataset.tracking, 'link');
      });
    });
  },
  methods: {
    setModalValues() {
      const { careLevel, status, appliedAt } = this.current;
      this.setActiveStatusItem(status ? status.toString() : '0');
      this.applicationCareLevel = careLevel;
      this.applicationDate = appliedAt;
    },

    setActiveStatusItem(value) {
      this.applicationDate = null;

      if (this.careLevelError && ['0', '1', '5'].includes(value)) {
        this.careLevelError = false;
      }

      const newStatusItems = this.statusItems;
      this.statusItems.map((si) => {
        si.selected = si.value === value;
      });

      this.statusItems = [...newStatusItems];
    },

    openContentSheet() {
      this.$store.dispatch(CONTENT_SHEET_CONTENT, {
        sheetId: `applications/${this.id}`,
      });
    },

    openEdithCarePopUp() {
      this.$store.dispatch(TOGGLE_EDITHCARE_POPUP);
      this.onTrackingClick('-edithCare-Pop-up_btn-öffnen Antrag stellen', 'button');
    },

    onClickCareLevel(i) {
      if (this.applicationCareLevel === i) {
        this.applicationCareLevel = null;
      } else {
        this.applicationCareLevel = i;
      }
    },

    isCareLevelError() {
      const status = ['2', '3', '4'];
      const ids = ['erstantrag', 'hoeherstufung'];
      return (
        status.includes(this.currentStatusItem.value) &&
        ids.includes(this.id) &&
        !this.applicationCareLevel
      );
    },

    isDateError() {
      const status = ['3'];
      const ids = ['pflegegeld', 'pflegesachleistungen', 'kombinationsleistung'];
      return (
        status.includes(this.currentStatusItem.value) &&
        ids.includes(this.id) &&
        !this.applicationDate
      );
    },

    openModal() {
      this.modal = true;
      this.onTrackingClick('-Antragsstatus aktualisieren-Pop-up_click-öffnen', 'element');
    },

    close(src = null) {
      this.loading = false;
      this.modal = false;
      if (src === 'button') {
        this.onTrackingClick('-Antragsstatus aktualisieren-Pop-up_btn-Abbrechen', 'button');
      }
      if (src === 'x') {
        this.onTrackingClick('-Antragsstatus aktualisieren-Pop-up_click-schließen', 'element');
      }
    },

    onSave() {
      if (this.isCareLevelError()) {
        this.careLevelError = true;
        return false;
      }

      if (this.isDateError()) {
        this.dateError = true;
        return false;
      }

      this.careLevelError = false;
      this.dateError = false;
      this.loading = true;

      const careRequest = {
        careLevel: this.applicationCareLevel,
        applied_at: this.applicationDate || null,
        status: parseInt(this.currentStatusItem.value),
        type: this.id,
      };

      this.$store.dispatch(APPLICATION_SAVE, { careRequest }).then(() => {
        const updateCareLevelOrClose = () => {
          if (this.willUpdateCareLevel) {
            this.updateCareLevel();
          } else {
            this.close();
          }
        };

        if (this.isCareLevelApplication) {
          updateCareLevelOrClose();
        } else {
          /**
           * Wenn Antrag genehmigt wurde, dann ändere auch den Wert im PLF
           */
          this.updateCareServices(careRequest.status)
            .then(updateCareLevelOrClose)
            .catch(this.close);
        }
        this.onTrackingClick('-Antragsstatus aktualisieren-Pop-up_btn-Speichern', 'button');
      });
    },

    healthRequest() {
      this.$store.dispatch(HEALTH_REQUEST).then(this.close).catch(this.close);
    },

    updateCareServices(status) {
      return new Promise((resolve, reject) => {
        const ids = ['pflegegeld', 'pflegesachleistungen', 'kombinationsleistung'];
        if (ids.includes(this.id)) {
          const updates = [];
          const { id: currentId } = this.currentBasicApplication;
          const others = ids.filter((id) => id !== currentId);
          if (currentId) {
            updates.push({
              id: currentId,
              value: 2,
            });
          }
          others.map((otherId) => {
            updates.push({
              id: otherId,
              value: null,
            });
          });

          let promise = Promise.resolve();
          updates.forEach((update) => {
            promise = promise.then(() => {
              return this.$store.dispatch(CARESERVICE_UPDATE_FACT, update);
            });
          });

          promise.then(resolve).catch(reject);
        } else {
          let value = status === 3 ? 2 : null;

          if (status !== 3 && this.careServiceFacts[this.id] === 1) {
            value = this.careServiceFacts[this.id];
          }

          this.$store
            .dispatch(CARESERVICE_UPDATE_FACT, {
              id: this.id,
              value,
            })
            .then(resolve)
            .catch(reject);
        }
      });
    },

    updateCareLevel() {
      const data = {
        personInNeedOfCareUuid: this.healthUuid,
        careLevel: this.applicationCareLevel,
        careLevelDate: this.applicationDate || null,
      };

      Vue.axios
        .post(ROUTE_API_UPDATE_CARE_LEVEL_DATA, {
          careLevelWizard: data,
        })
        .then(() => {
          /**
           * Wenn der neue Pflegegrad vom aktuellen
           * abweicht, dann setze die nächste Aufgaben P3-20 (Pflegeleistungen)
           */
          if (this.careLevel !== this.applicationCareLevel) {
            this.$store
              .dispatch(TASKS_UPDATE_CURRENT_TASK, 'P3-20')
              .then(this.healthRequest)
              .catch(this.close);
          } else {
            this.close();
          }
        })
        .catch(this.close);
    },

    openDownloadArea() {
      if (this.documents && this.documents.length > 0) {
        this.documents.length > 1
          ? this.$store.dispatch(CONTENT_SHEET_CONTENT, {
              sheetId: `applications/downloads`,
              data: {
                documents: this.documents,
                token: this.token,
              },
            })
          : this.createDownload();
      }
    },

    createDownload() {
      Vue.axios
        .post(
          ROUTE_GET_EDITHCARE_PDF,
          {
            token: this.token,
            interaction_id: this.documents[0].interaction_id,
          },
          {
            responseType: 'arraybuffer',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/pdf',
            },
          }
        )
        .then((response) => {
          this.createDownloadLink(response.data, this.documents[0]);
        });
    },

    onTrackingClick(labelText, element) {
      this.$emit('onTrackingClick', labelText, element);
    },
  },
};
</script>
